/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/EventsList/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import styles from "./EventsList.module.css";

import {
  generateAssetUrl,
  refreshToken,
  signOut,
  truncateText,
} from "../../utils/common.js";
import { AppContext } from "../../context/AppContext";
import { getUser } from "../../logic/user";
import { getAllEvents, getUserRoleData } from "../../logic/eventsList/index.js";

import { ReactComponent as CalendarIcon } from "../../assets/icons/EventsList/CalendarIcon.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/EventsList/AddIcon.svg";

import { use, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import Caintra_Logo_03 from "../../assets/Caintra_Logo_03.png";
import EventBanner from "../../assets/EventBanner.png";
import AssetImage from "../AssetImage";

const EventsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { changePermissions } = use(AppContext);

  const [eventsData, setEventsData] = useState([]);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!!token && token.trim() !== "undefined") {
      getData();
    }
  }, []);

  const getData = async () => {
    try {
      const userId = localStorage.getItem("userId");
      await getUserRoleData(userId, navigate, changePermissions);
      const userData = await getUser(userId);
      setUser(userData);

      const eventsData = await getAllEvents(userId, navigate, userData);
      setEventsData(eventsData);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const handleViewMore = () => {
    navigate("/events");
  };

  return (
    <div className={styles.eventsListSection}>
      <div className={styles.eventsListHeader}>
        <div className={styles.eventsListHeaderTitle}>
          <CalendarIcon className={styles.eventsListTitleIcon} />
          <h2 className={styles.eventsListTitle}>
            {t("components.eventsList.title")}
          </h2>
        </div>
      </div>

      <div className={styles.eventsList}>
        {eventsData.length === 0 && (
          <div className={styles.noEvents}>
            <p>{t("components.eventsList.noEvents")}</p>
          </div>
        )}
        {eventsData.length > 0 &&
          eventsData.map((event, index) => (
            <div key={index} className={styles.eventCard}>
              <AssetImage
                className={styles.eventImage}
                src={
                  event.imagenBanner
                    ? generateAssetUrl(event.imagenBanner)
                    : EventBanner
                }
                alt={t("common.bannerAlt")}
              />
              <div className={styles.eventInfo}>
                <div className={styles.eventHeader}>
                  <div className={styles.eventHeaderTitle}>
                    <div className={styles.messageItemAvatar}>
                      <img
                        className={styles.avatar}
                        src={Caintra_Logo_03}
                        alt={t("common.avatarAlt")}
                      />
                    </div>
                    <span className={styles.eventName}>{event.nombre}</span>
                  </div>
                </div>
                <div className={styles.eventDetails}>
                  <div className={styles.eventDate}>
                    <span className={styles.dayOfWeek}>
                      {dayjs(event.fechaEvento || event.fechaInicioEvento)
                        .utc()
                        .format("dddd")}
                    </span>
                    <span className={styles.day}>
                      {dayjs(event.fechaEvento || event.fechaInicioEvento)
                        .utc()
                        .format("DD")}
                    </span>
                    <span className={styles.monthAndYear}>
                      {dayjs(event.fechaEvento || event.fechaInicioEvento)
                        .utc()
                        .format("MMMM")}
                      {", "}
                      {dayjs(event.fechaEvento || event.fechaInicioEvento)
                        .utc()
                        .format("YYYY")}
                    </span>
                  </div>
                  <div className={styles.divider} />
                  <div className={styles.eventDescriptionContainer}>
                    <span className={styles.eventDescription}>
                      {truncateText(event.descripcion, 50)}
                    </span>
                  </div>
                </div>
                <div className={styles.viewMoreButtonContainer}>
                  <button
                    className={styles.viewMoreButton}
                    onClick={handleViewMore}
                  >
                    {t("components.eventsList.viewMore")}
                    <AddIcon />
                  </button>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default EventsList;
